// 親フレーム（Connect Note）から受け取った検索語を、キャンバス本文の表示文字と照合する純粋関数。
//
// DOM とストアには依存しない（描画は linkified-text.tsx が受け持つ）。照合規則は Connect Note の
// 通常記事の本文ハイライトにそろえ、閲覧者から見て記事とキャンバスで一致の基準が変わらないようにする。
//
// - 検索語は空白（半角・全角）で区切り、空要素を除き、小文字化して重複を除く
// - 比較は小文字化した文字列同士で行い、表示は元の文字をそのまま残す
// - 正規表現として解釈しない（メタ文字はそのままの文字として照合する）
// - 長い語から順に照合し、既に一致した範囲の中へ入れ子の一致を作らない
// - 語数と 1 語あたりの長さに上限を設け、極端な入力で描画が重くならないようにする

/** 照合に使う語数の上限。これを超えた分は無視する。 */
export const SEARCH_TERM_COUNT_LIMIT = 32;

/** 1 語あたりの文字数（UTF-16 コードユニット数）の上限。これを超える語は無視する。 */
export const SEARCH_TERM_LENGTH_LIMIT = 256;

/** 検索語で分割した表示文字の 1 区間。`matched` が true の区間だけを強調する。 */
export interface SearchHighlightSegment {
  readonly value: string;
  readonly matched: boolean;
}

/**
 * 元の文字列との index 対応を保ったまま小文字化する。
 *
 * `String.prototype.toLowerCase()` は一部の文字（例: `İ`）で長さが変わるため、そのまま使うと
 * 小文字化後の index を元テキストへ当てはめられず、強調範囲が 1 文字ずれる。長さが変わる文字は
 * 元の文字のまま残し、「小文字化した文字列の index ＝ 元テキストの index」を常に成立させる。
 */
function toLowerPreservingLength(text: string): string {
  let result = "";
  // コードポイント単位で回す（サロゲートペアを分割しない）。
  for (const character of text) {
    const lowered = character.toLowerCase();
    result += lowered.length === character.length ? lowered : character;
  }
  return result;
}

/**
 * 検索語の文字列を、照合に使う語の配列へ正規化する。
 * 強調しない（語が 1 つも無い）場合は空配列を返す。
 */
export function normalizeSearchTerms(query: string): readonly string[] {
  // JavaScript の \s は全角空白（U+3000）も含むため、半角・全角のどちらでも区切られる
  // （この前提は search-highlight.test.ts の「全角空白でも区切る」が固定する）。
  const rawTerms = query.split(/\s+/u);
  const terms: string[] = [];
  const seen = new Set<string>();
  for (const rawTerm of rawTerms) {
    if (rawTerm.length === 0 || rawTerm.length > SEARCH_TERM_LENGTH_LIMIT) continue;
    const term = toLowerPreservingLength(rawTerm);
    if (seen.has(term)) continue;
    seen.add(term);
    terms.push(term);
    if (terms.length >= SEARCH_TERM_COUNT_LIMIT) break;
  }
  return terms;
}

/**
 * 表示文字を、検索語に一致する区間と一致しない区間へ分割する。
 *
 * 語は {@link normalizeSearchTerms} が返した正規化済みのものを渡すこと。
 * 一致が無い場合は「全体が非一致の 1 区間」を返し、空文字列では空配列を返す。
 */
export function splitTextBySearchTerms(
  text: string,
  terms: readonly string[]
): readonly SearchHighlightSegment[] {
  if (text.length === 0) return [];
  if (terms.length === 0) return [{ matched: false, value: text }];

  // 長い語を先に試すことで、短い語の一致が長い語の一致の内側へ入れ子になるのを防ぐ。
  const orderedTerms = [...terms].sort((a, b) => b.length - a.length);
  const haystack = toLowerPreservingLength(text);

  const segments: SearchHighlightSegment[] = [];
  let plainStart = 0;
  let index = 0;
  while (index < haystack.length) {
    const hit = orderedTerms.find((term) => haystack.startsWith(term, index));
    if (hit === undefined) {
      index += 1;
      continue;
    }
    if (index > plainStart) {
      segments.push({ matched: false, value: text.slice(plainStart, index) });
    }
    segments.push({ matched: true, value: text.slice(index, index + hit.length) });
    index += hit.length;
    plainStart = index;
  }
  if (plainStart < text.length) {
    segments.push({ matched: false, value: text.slice(plainStart) });
  }
  return segments;
}
